import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Modal,
} from 'react-native';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { FONTS } from '../../constants/config';
import { CalendarEvent, CalendarDay } from './types';
import EventCard from './EventCard';

interface WeeklyGridProps {
  days: CalendarDay[];
  startHour: number;
  endHour: number;
  onEventPress?: (event: CalendarEvent) => void;
}

const HOUR_HEIGHT = 80;
const TIME_COLUMN_WIDTH = 48;
const MIN_DAY_WIDTH = 120;
const MAX_VISIBLE_OVERLAP = 4;

/** Convert "HH:MM" to fractional hours (e.g. "09:30" → 9.5) */
function timeToHours(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h + m / 60;
}

/** Detect overlapping events and assign column positions */
interface LayoutedEvent {
  event: CalendarEvent;
  column: number;
  totalColumns: number;
  overflowGroup?: CalendarEvent[]; // set only on the "view more" placeholder
}

function layoutOverlappingEvents(events: CalendarEvent[]): LayoutedEvent[] {
  if (events.length === 0) return [];

  // Sort by start time, then by end time descending (longer events first)
  const sorted = [...events].sort((a, b) => {
    const diff = timeToHours(a.startTime) - timeToHours(b.startTime);
    return diff !== 0 ? diff : timeToHours(b.endTime) - timeToHours(a.endTime);
  });

  // Build collision clusters: groups of events that overlap with each other
  const clusters: CalendarEvent[][] = [];
  let currentCluster: CalendarEvent[] = [sorted[0]];
  let clusterEnd = timeToHours(sorted[0].endTime);

  for (let i = 1; i < sorted.length; i++) {
    const evStart = timeToHours(sorted[i].startTime);
    if (evStart < clusterEnd) {
      // Overlaps with current cluster
      currentCluster.push(sorted[i]);
      clusterEnd = Math.max(clusterEnd, timeToHours(sorted[i].endTime));
    } else {
      clusters.push(currentCluster);
      currentCluster = [sorted[i]];
      clusterEnd = timeToHours(sorted[i].endTime);
    }
  }
  clusters.push(currentCluster);

  // Assign columns within each cluster
  const result: LayoutedEvent[] = [];
  for (const cluster of clusters) {
    if (cluster.length <= MAX_VISIBLE_OVERLAP) {
      // All fit side by side
      const totalColumns = cluster.length;
      cluster.forEach((ev, idx) => {
        result.push({ event: ev, column: idx, totalColumns });
      });
    } else {
      // Show first (MAX_VISIBLE_OVERLAP - 1) + a "view more" placeholder
      const visible = MAX_VISIBLE_OVERLAP - 1;
      const totalColumns = MAX_VISIBLE_OVERLAP;
      for (let i = 0; i < visible; i++) {
        result.push({ event: cluster[i], column: i, totalColumns });
      }
      // The last slot is a "+N more" trigger — use the first overflow event as placeholder
      result.push({
        event: cluster[visible],
        column: visible,
        totalColumns,
        overflowGroup: cluster.slice(visible),
      });
    }
  }
  return result;
}

/** Format hour number to display label */
function formatHourLabel(hour: number): string {
  if (hour === 0 || hour === 12) {
    return hour === 0 ? '12AM' : '12PM';
  }
  return hour < 12 ? `${hour}AM` : `${hour - 12}PM`;
}

export default function WeeklyGrid({
  days,
  startHour,
  endHour,
  onEventPress,
}: WeeklyGridProps) {
  const { colors } = useTheme();
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const [overflowModal, setOverflowModal] = useState<CalendarEvent[] | null>(null);

  const totalHours = endHour - startHour;
  const hours = Array.from({ length: totalHours + 1 }, (_, i) => startHour + i);

  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={{ flexGrow: 1 }}
    >
      <View style={{ flex: 1, minWidth: TIME_COLUMN_WIDTH + days.length * MIN_DAY_WIDTH }}>
        {/* Day headers row */}
        <View style={[styles.headerRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
          {/* Empty cell above time column */}
          <View style={{ width: TIME_COLUMN_WIDTH, flexShrink: 0 }} />

          {/* Day name + number headers */}
          {days.map((day) => (
            <View
              key={day.date.toISOString()}
              style={[
                styles.dayHeader,
                day.isToday && { backgroundColor: `${'#a9211f'}12` },
              ]}
            >
              <Text
                style={[
                  styles.dayName,
                  { color: day.isToday ? '#a9211f' : colors.textMuted },
                ]}
              >
                {day.dayName}
              </Text>
              <View
                style={[
                  styles.dayNumberWrapper,
                  day.isToday && { backgroundColor: '#a9211f' },
                ]}
              >
                <Text
                  style={[
                    styles.dayNumber,
                    { color: day.isToday ? '#FFFFFF' : colors.text },
                  ]}
                >
                  {day.dayNumber}
                </Text>
              </View>
            </View>
          ))}
        </View>

        {/* Grid body */}
        <View style={[styles.gridBody, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
          {/* Time labels column */}
          <View style={{ width: TIME_COLUMN_WIDTH, flexShrink: 0 }}>
            {hours.map((hour) => (
              <View key={hour} style={[styles.timeLabel, { height: HOUR_HEIGHT }]}>
                <Text style={[styles.timeLabelText, { color: colors.textLight }]}>
                  {formatHourLabel(hour)}
                </Text>
              </View>
            ))}
          </View>

          {/* Day columns */}
          {days.map((day) => (
            <View
              key={day.date.toISOString()}
              style={[
                styles.dayColumn,
                day.isToday && { backgroundColor: `${'#a9211f'}0A` },
              ]}
            >
              {/* Hourly grid lines (horizontal only) */}
              {hours.map((hour) => (
                <View
                  key={hour}
                  style={[
                    styles.hourCell,
                    { height: HOUR_HEIGHT, borderBottomColor: colors.borderLight },
                  ]}
                />
              ))}

              {/* Event cards (absolutely positioned, with overlap handling) */}
              {layoutOverlappingEvents(day.events).map((item) => {
                const eventStart = timeToHours(item.event.startTime);
                const eventEnd = timeToHours(item.event.endTime);
                const top = (eventStart - startHour) * HOUR_HEIGHT;
                const height = Math.max((eventEnd - eventStart) * HOUR_HEIGHT - 4, 24);

                // Calculate horizontal position for side-by-side layout
                const slotWidth = 100 / item.totalColumns;
                const leftPct = isRTL
                  ? (item.totalColumns - item.column - 1) * slotWidth
                  : item.column * slotWidth;

                if (item.overflowGroup) {
                  // "+N more" button
                  return (
                    <TouchableOpacity
                      key={`overflow-${item.event.id}`}
                      style={[
                        styles.eventWrapper,
                        {
                          top,
                          height: height + 4,
                          left: `${leftPct}%` as any,
                          right: undefined,
                          width: `${slotWidth}%` as any,
                        },
                      ]}
                      activeOpacity={0.7}
                      onPress={() => setOverflowModal(item.overflowGroup!)}
                    >
                      <View style={[styles.overflowBtn, { backgroundColor: colors.cardBorder }]}>
                        <Text style={[styles.overflowText, { color: colors.textPrimary }]}>
                          +{item.overflowGroup.length}
                        </Text>
                      </View>
                    </TouchableOpacity>
                  );
                }

                return (
                  <View
                    key={item.event.id}
                    style={[
                      styles.eventWrapper,
                      {
                        top,
                        height: height + 4,
                        left: `${leftPct}%` as any,
                        right: undefined,
                        width: `${slotWidth}%` as any,
                      },
                    ]}
                  >
                    <EventCard
                      event={item.event}
                      height={height}
                      onPress={onEventPress}
                    />
                  </View>
                );
              })}
            </View>
          ))}
        </View>
      </View>

      {/* Overflow modal */}
      {overflowModal && (
        <Modal transparent animationType="fade" visible onRequestClose={() => setOverflowModal(null)}>
          <TouchableOpacity
            style={styles.modalBackdrop}
            activeOpacity={1}
            onPress={() => setOverflowModal(null)}
          >
            <View style={[styles.modalContent, { backgroundColor: colors.card }]}>
              <Text style={[styles.modalTitle, { color: colors.textPrimary }]}>
                {isRTL ? 'المزيد من الأنشطة' : 'More Activities'}
              </Text>
              <ScrollView style={{ maxHeight: 300 }}>
                {overflowModal.map((event) => (
                  <TouchableOpacity
                    key={event.id}
                    style={styles.modalEventRow}
                    onPress={() => { setOverflowModal(null); onEventPress?.(event); }}
                  >
                    <EventCard event={event} height={56} onPress={onEventPress} />
                  </TouchableOpacity>
                ))}
              </ScrollView>
              <TouchableOpacity
                style={[styles.modalCloseBtn, { backgroundColor: '#A9211F' }]}
                onPress={() => setOverflowModal(null)}
              >
                <Text style={styles.modalCloseBtnText}>{isRTL ? 'إغلاق' : 'Close'}</Text>
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        </Modal>
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  headerRow: {
  },
  dayHeader: {
    flex: 1,
    minWidth: MIN_DAY_WIDTH,
    alignItems: 'center',
    paddingVertical: 10,
    gap: 4,
  },
  dayName: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    lineHeight: 16,
  },
  dayNumber: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    lineHeight: 20,
  },
  dayNumberWrapper: {
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
  },
  gridBody: {
  },
  timeLabel: {
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
    paddingRight: 8,
  },
  timeLabelText: {
    fontSize: 11,
    fontFamily: FONTS.regular,
    lineHeight: 14,
    marginTop: -7,
  },
  dayColumn: {
    flex: 1,
    minWidth: MIN_DAY_WIDTH,
    position: 'relative',
  },
  hourCell: {
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  eventWrapper: {
    position: 'absolute',
    left: 3,
    right: 3,
    zIndex: 1,
    padding: 2,
  },
  overflowBtn: {
    flex: 1,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    margin: 2,
  },
  overflowText: {
    fontSize: 13,
    fontFamily: FONTS.medium,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    width: 340,
    borderRadius: 12,
    padding: 20,
    gap: 12,
  },
  modalTitle: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    textAlign: 'center',
  },
  modalEventRow: {
    marginBottom: 8,
  },
  modalCloseBtn: {
    borderRadius: 8,
    paddingVertical: 10,
    alignItems: 'center',
    marginTop: 4,
  },
  modalCloseBtnText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontFamily: FONTS.medium,
  },
});
